// Blueprint editor state (requirements 3.1-3.6; design 3.3, 3.4). Pure reducer so it can be tested directly.
import {
  formToSchema,
  mergeForPreview,
  schemaToForm,
  type BlueprintFieldNode,
  type BlueprintForm,
  type GroupField,
  type ScalarField,
  type TableField,
} from '@bda/shared';

export type Mode = 'form' | 'json';

export interface EditorState {
  form: BlueprintForm;
  mode: Mode;
  jsonText: string;
  jsonError: string | null;
  unsupported: string[];
  dirty: boolean;
  /** Run whose stored document is the Editor's test document (a Suggest_Run or a `?testRun=` run); Req 4.4, 7.3. */
  testRunId: string | null;
}

/** Path to a node: [topIndex] or [topIndex, childIndex] for group fields and table columns. */
export type NodePath = [number] | [number, number];

export type Action =
  | { type: 'reset'; form: BlueprintForm; dirty?: boolean }
  | { type: 'meta'; field: 'name' | 'documentType' | 'description'; value: string }
  | { type: 'add'; kind: BlueprintFieldNode['kind'] }
  | { type: 'addChild'; parent: number }
  | {
      type: 'update';
      path: NodePath;
      patch: Partial<ScalarField> & Partial<Omit<GroupField, 'fields'>> & Partial<Omit<TableField, 'columns'>>;
    }
  | { type: 'remove'; path: NodePath }
  | { type: 'move'; path: NodePath; delta: -1 | 1 }
  | { type: 'acceptSuggestions'; fields: BlueprintFieldNode[]; sampleRunId: string }
  | { type: 'toJson' }
  | { type: 'jsonText'; text: string }
  | { type: 'toForm' }
  | { type: 'saved' };

export const emptyForm = (): BlueprintForm => ({ name: '', documentType: '', description: '', fields: [] });

export const initialState = (form: BlueprintForm = emptyForm(), testRunId: string | null = null): EditorState => ({
  form,
  mode: 'form',
  jsonText: '',
  jsonError: null,
  unsupported: [],
  dirty: false,
  testRunId,
});

const newScalar = (n: number): ScalarField => ({
  kind: 'field',
  name: `field${n}`,
  type: 'string',
  instruction: '',
  inferenceType: 'explicit',
});

function uniqueName(existing: string[], base: string): string {
  const lower = new Set(existing.map((e) => e.toLowerCase()));
  for (let i = 1; ; i++) if (!lower.has(`${base}${i}`.toLowerCase())) return `${base}${i}`;
}

function move<T>(list: T[], i: number, delta: number): T[] {
  const j = i + delta;
  if (j < 0 || j >= list.length) return list;
  const copy = [...list];
  [copy[i], copy[j]] = [copy[j]!, copy[i]!];
  return copy;
}

const children = (n: BlueprintFieldNode): ScalarField[] | null =>
  n.kind === 'group' ? n.fields : n.kind === 'table' ? n.columns : null;
const withChildren = (n: BlueprintFieldNode, list: ScalarField[]): BlueprintFieldNode =>
  n.kind === 'group' ? { ...n, fields: list } : n.kind === 'table' ? { ...n, columns: list } : n;

function mapFields(state: EditorState, fn: (fields: BlueprintFieldNode[]) => BlueprintFieldNode[]): EditorState {
  return { ...state, form: { ...state.form, fields: fn(state.form.fields) }, dirty: true };
}

export function reducer(state: EditorState, action: Action): EditorState {
  switch (action.type) {
    case 'reset':
      return { ...initialState(action.form, state.testRunId), dirty: action.dirty ?? false };
    case 'meta':
      return { ...state, form: { ...state.form, [action.field]: action.value }, dirty: true };
    case 'add': {
      const names = state.form.fields.map((f) => f.name);
      const node: BlueprintFieldNode =
        action.kind === 'field'
          ? { ...newScalar(0), name: uniqueName(names, 'field') }
          : action.kind === 'group'
            ? { kind: 'group', name: uniqueName(names, 'group'), description: '', fields: [newScalar(1)] }
            : { kind: 'table', name: uniqueName(names, 'table'), instruction: '', columns: [newScalar(1)] };
      return mapFields(state, (fs) => [...fs, node]);
    }
    case 'addChild':
      return mapFields(state, (fs) =>
        fs.map((n, i) => {
          const list = children(n);
          if (i !== action.parent || !list) return n;
          return withChildren(n, [
            ...list,
            {
              ...newScalar(0),
              name: uniqueName(
                list.map((c) => c.name),
                n.kind === 'table' ? 'column' : 'field',
              ),
            },
          ]);
        }),
      );
    case 'update':
      return mapFields(state, (fs) =>
        fs.map((n, i) => {
          if (i !== action.path[0]) return n;
          if (action.path.length === 1) return { ...n, ...action.patch } as BlueprintFieldNode;
          const list = children(n);
          if (!list) return n;
          return withChildren(
            n,
            list.map((c, j) => (j === action.path[1] ? ({ ...c, ...action.patch } as ScalarField) : c)),
          );
        }),
      );
    case 'remove':
      return mapFields(state, (fs) =>
        action.path.length === 1
          ? fs.filter((_, i) => i !== action.path[0])
          : fs.map((n, i) => {
              const list = children(n);
              return i === action.path[0] && list
                ? withChildren(
                    n,
                    list.filter((_, j) => j !== action.path[1]),
                  )
                : n;
            }),
      );
    case 'move':
      return mapFields(state, (fs) =>
        action.path.length === 1
          ? move(fs, action.path[0], action.delta)
          : fs.map((n, i) => {
              const list = children(n);
              return i === action.path[0] && list ? withChildren(n, move(list, action.path[1]!, action.delta)) : n;
            }),
      );
    case 'acceptSuggestions':
      // Same merge as a modal preview (Req 1.6, 1.9); the Sample_Document becomes the test document (Req 4.4).
      return { ...mapFields(state, (fs) => mergeForPreview(fs, action.fields)), testRunId: action.sampleRunId };
    case 'toJson':
      return {
        ...state,
        mode: 'json',
        jsonText: JSON.stringify(formToSchema(state.form), null, 2),
        jsonError: null,
        unsupported: [],
      };
    case 'jsonText':
      return { ...state, jsonText: action.text, dirty: true, jsonError: null };
    case 'toForm': {
      let parsed: unknown;
      try {
        parsed = JSON.parse(state.jsonText);
      } catch (e) {
        return { ...state, jsonError: `The JSON is not valid: ${(e as Error).message}` };
      }
      const res = schemaToForm(parsed, state.form.name);
      if (res.unsupported) return { ...state, unsupported: res.unsupported, jsonError: null };
      return { ...state, mode: 'form', form: { ...res.form, name: state.form.name }, unsupported: [], jsonError: null };
    }
    case 'saved':
      return { ...state, dirty: false };
  }
}

/** The schema that would be saved, from whichever mode is active. */
export function currentSchema(state: EditorState): { schema: unknown } | { error: string } {
  if (state.mode === 'form') return { schema: formToSchema(state.form) };
  try {
    return { schema: JSON.parse(state.jsonText) };
  } catch (e) {
    return { error: `The JSON is not valid: ${(e as Error).message}` };
  }
}

// ---------- draft mirror (design 3.3) ----------

export const draftKey = (id: string | undefined): string => `draft:${id ?? 'new'}`;

export function saveDraft(id: string | undefined, state: EditorState, revision: number | null): void {
  window.sessionStorage.setItem(draftKey(id), JSON.stringify({ state, revision, at: Date.now() }));
}

export function loadDraft(id: string | undefined): { state: EditorState; revision: number | null } | null {
  const raw = window.sessionStorage.getItem(draftKey(id));
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as { state: EditorState; revision: number | null };
    if (!v.state?.form) return null;
    // Drafts written before testRunId existed have none (Req 7.3).
    return { ...v, state: { ...v.state, testRunId: v.state.testRunId ?? null } };
  } catch {
    return null;
  }
}

export const clearDraft = (id: string | undefined): void => window.sessionStorage.removeItem(draftKey(id));
